import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CustomDatePicker from '../shared/CustomDatePicker';
import { getAnalyticsSummary, getSalesPersonAnalyticsSummary } from '../../../../services/api/analyticsApi';
import { getSalesPersons } from '../../../../services/api/lookupApi';
import {
    Calendar,
    Download,
    Printer,
    TrendingUp,
    Users,
    DollarSign,
    ChevronRight,
    FileText,
    ArrowRight,
    XCircle,
    ShoppingBag,
    CreditCard,
    MapPin,
    Hash,
    Clock,
    User,
    Search,
    Wallet
} from 'lucide-react';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import { downloadConsolidatedPdf } from '../../../../services/api/dailySalesReportApi';

import { useAuth } from '../../../../context/AuthContext';
import { normalizeRole } from '../../../../config/roles';

const DailyReportsView = ({ supplierLogoUrl, companyName, role }) => {
    const { user } = useAuth();
    const isSalesPerson = role === 'SALES_PERSON';

    const [dateRange, setDateRange] = useState({
        start: format(startOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        end: format(endOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'")
    });
    const [isAllMode, setIsAllMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [staffList, setStaffList] = useState([]);
    const [staffFilter, setStaffFilter] = useState('');
    const [reports, setReports] = useState([]);
    const [detailedData, setDetailedData] = useState({});

    // Customer search activity states
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerActivity, setCustomerActivity] = useState(null);
    const [searchingActivity, setSearchingActivity] = useState(false);
    const [activeActivityTab, setActiveActivityTab] = useState('visits');
    const [customerSearchError, setCustomerSearchError] = useState(null);

    const fetchData = useCallback(async () => {
        if (isSalesPerson && !user?.publicId) {
            console.log("DailyReportsView: Sales person role detected but profile (publicId) not yet loaded. Waiting...");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const { getDailySalesReports, getDailySalesReport } = await import('../../../../services/api/dailySalesReportApi');

            // When isAllMode is active, omit date params so the backend returns all records
            const analyticsStart = isAllMode ? undefined : dateRange.start;
            const analyticsEnd   = isAllMode ? undefined : dateRange.end;
            const reportsParams = {
                salesPersonPublicId: isSalesPerson ? user?.publicId : staffFilter,
                ...(isAllMode ? {} : {
                    startDate: dateRange.start.split('T')[0],
                    endDate:   dateRange.end.split('T')[0],
                })
            };

            const [summary, reportsList] = await Promise.all([
                isSalesPerson
                    ? getSalesPersonAnalyticsSummary(analyticsStart, analyticsEnd)
                    : getAnalyticsSummary(analyticsStart, analyticsEnd),
                getDailySalesReports(reportsParams)
            ]);

            setData(summary);
            setReports(reportsList);

            // Populate detailsMap directly from reportsList to avoid parallel request bottleneck
            const detailsMap = {};
            reportsList.forEach(r => {
                detailsMap[r.publicId] = r;
            });
            setDetailedData(detailsMap);
        } catch (err) {
            console.error("Failed to fetch report data:", err);
            setError("Failed to load report data. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [dateRange, isAllMode, staffFilter, isSalesPerson, user]);

    const groupedReports = useMemo(() => {
        const groups = {};
        reports.forEach(r => {
            const date = r.reportDate;
            if (!groups[date]) groups[date] = [];
            groups[date].push(r);
        });
        return Object.keys(groups)
            .sort((a, b) => new Date(a) - new Date(b))  // ascending: oldest date first
            .map(date => ({
                date,
                // Within date: sort by submission time ascending (oldest entry first, newest last)
                reports: groups[date].sort((a, b) => {
                    const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return tA - tB;
                })
            }));
    }, [reports]);



    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!customerSearch || customerSearch.trim().length < 2) {
            setCustomerActivity(null);
            setCustomerSearchError(null);
            return;
        }

        const delayDebounce = setTimeout(async () => {
            setSearchingActivity(true);
            setCustomerSearchError(null);
            try {
                const { getCustomerActivity } = await import('../../../../services/api/dailySalesReportApi');
                const result = await getCustomerActivity(customerSearch.trim());
                setCustomerActivity(result);
            } catch (err) {
                console.error("Failed to load customer activity:", err);
                setCustomerSearchError("Failed to fetch client history.");
            } finally {
                setSearchingActivity(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [customerSearch]);

    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const list = await getSalesPersons();
                setStaffList(list);
            } catch (err) {
                console.error("Failed to fetch staff:", err);
            }
        };
        fetchStaff();
    }, []);

    const handlePrint = () => {
        window.print();
    };

    const handleDateChange = (e, field) => {
        const val = e.target.value;
        if (!val) return;

        const date = new Date(val);
        const iso = field === 'start'
            ? format(startOfDay(date), "yyyy-MM-dd'T'HH:mm:ss'Z'")
            : format(endOfDay(date), "yyyy-MM-dd'T'HH:mm:ss'Z'");

        setDateRange(prev => ({ ...prev, [field]: iso }));
    };

    const summaryCards = useMemo(() => {
        // Calculate totals from reports list for higher accuracy on daily operations
        const totals = reports.reduce((acc, r) => ({
            sales: acc.sales + (r.totalSaleSummary || 0),
            cash: acc.cash + (r.totalCashAvailable || 0),
            expenses: acc.expenses + (r.totalExpenses || 0),
            visits: acc.visits + (r.totalVisits || 0)
        }), { sales: 0, cash: 0, expenses: 0, visits: 0 });

        return [
            { label: 'TOTAL SALES', value: totals.sales, icon: TrendingUp, gradient: 'bg-gradient-to-br from-blue-500 to-indigo-600' },
            { label: 'TOTAL CASH', value: totals.cash, icon: Wallet, gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600' },
            { label: 'TOTAL EXPENSES', value: totals.expenses, icon: CreditCard, gradient: 'bg-gradient-to-br from-rose-500 to-pink-600' },
            { label: 'TOTAL VISITS', value: totals.visits, icon: Users, gradient: 'bg-gradient-to-br from-purple-500 to-violet-600', noCurrency: true },
        ];
    }, [reports]);

    const applyQuickFilter = (type) => {
        if (type === 'all') {
            setIsAllMode(true);
            return;
        }

        setIsAllMode(false);
        let start, end;
        const now = new Date();
        
        switch(type) {
            case 'today':
                start = startOfDay(now);
                end = endOfDay(now);
                break;
            case 'yesterday':
                start = startOfDay(subDays(now, 1));
                end = endOfDay(subDays(now, 1));
                break;
            default:
                return;
        }

        setDateRange({
            start: format(start, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
            end: format(end, "yyyy-MM-dd'T'HH:mm:ss'Z'")
        });
    };

    const renderCustomerActivityView = () => {
        if (searchingActivity) {
            return (
                <div className="h-64 flex flex-col items-center justify-center text-[var(--color-text-muted)]">
                    <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                    <p className="font-bold text-sm uppercase tracking-wider">Searching Client History...</p>
                </div>
            );
        }

        if (customerSearchError) {
            return (
                <div className="max-w-4xl mx-auto p-6 bg-red-50 text-red-600 rounded-2xl border border-red-100 text-xs font-bold text-center">
                    {customerSearchError}
                </div>
            );
        }

        if (!customerActivity) return null;

        const visits = customerActivity.visits || [];
        const sales = customerActivity.sales || [];

        return (
            <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-top-4 duration-300">
                {/* Header card with summary counts */}
                <div className="bg-gradient-to-r from-gray-900 to-black text-white p-6 rounded-3xl shadow-xl flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                    <div>
                        <span className="text-[9px] font-black text-blue-400 uppercase tracking-[0.2em] mb-1 block">Activity History</span>
                        <h2 className="text-2xl font-black tracking-tight">History for "{customerSearch}"</h2>
                        <p className="text-[10px] font-semibold text-gray-400 mt-1 uppercase tracking-wider">
                            Found {visits.length} Visits & {sales.length} Transactions
                        </p>
                    </div>
                    <button
                        onClick={() => setCustomerSearch('')}
                        className="self-start sm:self-center px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-black text-[10px] uppercase tracking-widest rounded-xl transition-all active:scale-95 border border-white/10"
                    >
                        Back to Daily Reports
                    </button>
                </div>

                {/* Sub-tabs switcher */}
                <div className="flex gap-4 border-b border-[var(--color-border)] px-2">
                    <button
                        onClick={() => setActiveActivityTab('visits')}
                        className={`pb-3 px-1 text-xs font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${
                            activeActivityTab === 'visits'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-900'
                        }`}
                    >
                        <MapPin size={14} />
                        Visits Log ({visits.length})
                    </button>
                    <button
                        onClick={() => setActiveActivityTab('sales')}
                        className={`pb-3 px-1 text-xs font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${
                            activeActivityTab === 'sales'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-900'
                        }`}
                    >
                        <ShoppingBag size={14} />
                        Sales Transactions ({sales.length})
                    </button>
                </div>

                {/* Tab Content */}
                <div className="space-y-6">
                    {activeActivityTab === 'visits' ? (
                        visits.length === 0 ? (
                            <div className="bg-white rounded-3xl p-12 text-center border border-dashed border-gray-200">
                                <p className="text-gray-400 font-bold uppercase text-xs">No visit records logged for "{customerSearch}"</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden p-6 space-y-6">
                                <div className="flow-root">
                                    <ul className="-mb-8">
                                        {visits.map((v, vIdx) => (
                                            <li key={v.publicId || vIdx}>
                                                <div className="relative pb-8">
                                                    {vIdx !== visits.length - 1 ? (
                                                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                                                    ) : null}
                                                    <div className="relative flex space-x-3">
                                                        <div>
                                                            <span className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                                                                <User size={16} />
                                                            </span>
                                                        </div>
                                                        <div className="flex-1 min-w-0 pt-1.5">
                                                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                                                <div>
                                                                    <p className="text-sm font-black text-gray-900 flex items-center gap-2">
                                                                        {v.clientName}
                                                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px] font-black uppercase">
                                                                            {v.visitType}
                                                                        </span>
                                                                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                                                                            v.outcomeNotes === 'Order Placed' ? 'bg-green-100 text-green-700' :
                                                                            v.outcomeNotes === 'Rejected' ? 'bg-red-100 text-red-700' :
                                                                            v.outcomeNotes ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                                                        }`}>
                                                                            {v.outcomeNotes || 'No Outcome'}
                                                                        </span>
                                                                    </p>
                                                                    <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">
                                                                        Logged by {(v.salesPersonName && v.salesPersonName !== 'Unknown Staff') ? v.salesPersonName : 'Staff Member'} {v.time ? `@ ${v.time}` : ''}
                                                                    </p>
                                                                </div>
                                                                <div className="text-right text-xs font-black text-gray-500 uppercase">
                                                                    {v.reportDate ? format(new Date(v.reportDate), 'dd/MM/yyyy') : '-'}
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 text-xs text-gray-700 bg-gray-50 p-4 rounded-2xl border border-gray-100 font-medium">
                                                                <p className="font-bold text-gray-900 mb-1">Discussion Notes:</p>
                                                                <p className="whitespace-pre-line leading-relaxed">{v.notes || 'No comments logged.'}</p>
                                                                
                                                                {v.followUpDetails && (
                                                                    <div className="mt-3 pt-3 border-t border-gray-200/60 text-[10px] text-blue-600 font-black uppercase tracking-wider flex items-center gap-1.5">
                                                                        <Clock size={12} />
                                                                        Scheduled Follow-up: {format(new Date(v.followUpDetails), 'dd/MM/yyyy')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )
                    ) : (
                        sales.length === 0 ? (
                            <div className="bg-white rounded-3xl p-12 text-center border border-dashed border-gray-200">
                                <p className="text-gray-400 font-bold uppercase text-xs">No transaction records logged for "{customerSearch}"</p>
                            </div>
                        ) : (
                            <div className="border border-slate-200 rounded-3xl overflow-hidden bg-white shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[620px] text-[11px] text-left border-collapse">
                                        <thead className="bg-blue-50/80 text-blue-800 border-y border-blue-100">
                                            <tr>
                                                <th className="px-5 py-3 font-black uppercase tracking-wider">Client</th>
                                                <th className="px-5 py-3 font-black uppercase tracking-wider">Order / Invoice</th>
                                                <th className="px-5 py-3 font-black text-right uppercase tracking-wider">B2B Cash</th>
                                                <th className="px-5 py-3 font-black text-right uppercase tracking-wider">B2B Bank</th>
                                                <th className="px-5 py-3 font-black text-right uppercase tracking-wider">Cash</th>
                                                <th className="px-5 py-3 font-black text-right uppercase tracking-wider">Bank</th>
                                                <th className="px-5 py-3 font-black text-right uppercase tracking-wider">Credit</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {sales.map((s, idx) => (
                                                <tr key={s.publicId || idx} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-5 py-3 font-bold text-slate-800 whitespace-nowrap">
                                                        <div>{s.clientName}</div>
                                                        <div className="text-[10px] text-slate-400 font-bold mt-0.5 uppercase tracking-wider">
                                                            {s.reportDate ? format(new Date(s.reportDate), 'dd/MM/yyyy') : '-'} • {s.salesPersonName}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-3 text-slate-500 font-medium whitespace-nowrap">
                                                        {(() => {
                                                            const orderRef = s.orderNo ? s.orderNo : null;
                                                            const invRef = s.invoiceNo ? s.invoiceNo : null;
                                                            if (!orderRef && !invRef) return <span className="text-slate-400">-</span>;
                                                            return (
                                                                <div>
                                                                    {orderRef && (
                                                                        <span className="font-bold text-slate-900 block text-xs">
                                                                            <span className="text-[10px] text-blue-600 font-bold mr-1">Order</span> {orderRef.replace(/^#/, '')}
                                                                        </span>
                                                                    )}
                                                                    {invRef && (
                                                                        <span className="text-[10px] text-slate-500 block mt-0.5">
                                                                            <span className="text-[9px] text-slate-400 font-normal mr-1">Inv:</span>{invRef}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="px-5 py-3 text-right font-bold text-slate-700">{s.b2bCashAmount != null ? Number(s.b2bCashAmount).toFixed(2) : '-'}</td>
                                                    <td className="px-5 py-3 text-right font-bold text-slate-700">{s.b2bBankAmount != null ? Number(s.b2bBankAmount).toFixed(2) : '-'}</td>
                                                    <td className="px-5 py-3 text-right font-bold text-slate-700">{s.cashAmount != null ? Number(s.cashAmount).toFixed(2) : '-'}</td>
                                                    <td className="px-5 py-3 text-right font-bold text-blue-600">{s.bankAmount != null ? Number(s.bankAmount).toFixed(2) : '-'}</td>
                                                    <td className="px-5 py-3 text-right font-bold text-slate-700">{s.creditAmount != null ? Number(s.creditAmount).toFixed(2) : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex-1 flex flex-col h-full bg-[var(--color-bg-primary)] overflow-hidden">
            {/* Header - Screen Only */}
            <div className="p-4 md:p-6 border-b border-[var(--color-border)] flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-black text-[var(--color-text-primary)] flex items-center gap-2">
                            <Printer className="text-blue-500" />
                            Daily Reports
                        </h1>
                        <button
                            onClick={() => downloadConsolidatedPdf(
                                isAllMode ? undefined : dateRange.start.split('T')[0],
                                isAllMode ? undefined : dateRange.end.split('T')[0],
                                isSalesPerson ? (user?.publicId || user?.id) : staffFilter
                            )}
                            className="flex items-center justify-center w-8 h-8 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white border border-blue-100 rounded-lg transition-all shadow-sm hover:shadow active:scale-95 shrink-0"
                            title="Export PDF Report"
                        >
                            <Download size={16} />
                        </button>
                    </div>
                    <p className="text-[var(--color-text-muted)] text-sm mt-1">
                        {isSalesPerson ? 'View your performance logs and exports' : 'Generate and export financial performance reports'}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex items-center bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-3 py-1.5 shadow-sm min-w-[200px]">
                        <Search size={16} className="text-gray-400 mr-2 shrink-0" />
                        <input
                            type="text"
                            placeholder="Search Client / Prospect..."
                            className="bg-transparent border-none outline-none text-xs font-bold text-[var(--color-text-primary)] focus:ring-0 p-0 flex-1 min-w-0 placeholder-gray-400"
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                        />
                        {customerSearch && (
                            <button
                                onClick={() => setCustomerSearch('')}
                                className="ml-2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none shrink-0"
                                title="Clear Search"
                            >
                                <XCircle size={14} />
                            </button>
                        )}
                    </div>
                    <CustomDatePicker
                        mode="range"
                        disabled={isAllMode}
                        startDate={dateRange.start.split('T')[0]}
                        endDate={dateRange.end.split('T')[0]}
                        onStartChange={(val) => { setIsAllMode(false); handleDateChange({ target: { value: val } }, 'start'); }}
                        onEndChange={(val) => { setIsAllMode(false); handleDateChange({ target: { value: val } }, 'end'); }}
                    />
                    <div className="flex items-center gap-1 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl p-1 shadow-sm">
                        <button
                            onClick={() => applyQuickFilter('today')}
                            className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                                !isAllMode &&
                                format(new Date(dateRange.start), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') && 
                                format(new Date(dateRange.end), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                                ? 'bg-blue-600 text-white shadow-md' 
                                : 'text-gray-500 hover:bg-gray-100'
                            }`}
                        >
                            Today
                        </button>
                        <button
                            onClick={() => applyQuickFilter('yesterday')}
                            className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                                !isAllMode &&
                                format(new Date(dateRange.start), 'yyyy-MM-dd') === format(subDays(new Date(), 1), 'yyyy-MM-dd') &&
                                format(new Date(dateRange.end), 'yyyy-MM-dd') === format(subDays(new Date(), 1), 'yyyy-MM-dd')
                                ? 'bg-blue-600 text-white shadow-md' 
                                : 'text-gray-500 hover:bg-gray-100'
                            }`}
                        >
                            Yesterday
                        </button>
                        <button
                            onClick={() => applyQuickFilter('all')}
                            className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                                isAllMode
                                ? 'bg-blue-600 text-white shadow-md' 
                                : 'text-gray-500 hover:bg-gray-100'
                            }`}
                        >
                            All
                        </button>
                    </div>

                    {!isSalesPerson && (
                        <div className="h-8 w-px bg-gray-200 mx-1" />
                    )}

                    {!isSalesPerson && (
                        <select
                            value={staffFilter}
                            onChange={(e) => setStaffFilter(e.target.value)}
                            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest shadow-sm focus:ring-2 focus:ring-blue-500/20"
                        >
                            <option value="">All Staff</option>
                            {staffList.map(s => (
                                <option key={s.publicId} value={s.publicId}>{s.name}</option>
                            ))}
                        </select>
                    )}


                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar print:p-0 print:overflow-visible">
                {customerSearch ? (
                    renderCustomerActivityView()
                ) : loading && !data ? (
                    <div className="h-64 flex flex-col items-center justify-center text-[var(--color-text-muted)] print:hidden">
                        <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                        <p className="font-bold">Generating Report Data...</p>
                    </div>
                ) : data ? (
                    <div className="max-w-6xl mx-auto space-y-12 print:space-y-6">

                        {/* Report Print Header - Visible Only on Print */}
                        <div className="hidden print:flex items-center justify-between border-b-2 border-gray-100 pb-6 mb-8">
                            <div className="flex items-center gap-4">
                                {supplierLogoUrl ? (
                                    <img src={supplierLogoUrl} alt="Logo" className="w-16 h-16 rounded-xl object-cover border border-gray-100 shadow-sm" />
                                ) : (
                                    <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center text-white text-2xl font-black">
                                        {companyName?.charAt(0) || 'S'}
                                    </div>
                                )}
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900">{companyName || 'Supplier Portal'}</h2>
                                    <p className="text-gray-500 font-bold tracking-wider uppercase text-xs">Financial & Performance Report</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">Report Period</p>
                                <p className="text-gray-900 font-black">
                                    {format(new Date(dateRange.start), 'MMM dd, yyyy')} - {format(new Date(dateRange.end), 'MMM dd, yyyy')}
                                </p>
                                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mt-2">Generated On</p>
                                <p className="text-gray-600 text-xs font-bold">{format(new Date(), 'MMM dd, yyyy HH:mm')}</p>
                            </div>
                        </div>

                        {/* Financial Summary Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                            {summaryCards.map((card, idx) => {
                                const Icon = card.icon;
                                const formattedVal = card.noCurrency 
                                    ? (Number(card.value) || 0).toLocaleString('en-US') 
                                    : `AED ${(Number(card.value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                const valStr = String(formattedVal);
                                const len = valStr.length;

                                let textSizeClass = 'text-lg sm:text-2xl';
                                if (len > 16) {
                                    textSizeClass = 'text-xs min-[380px]:text-sm sm:text-lg';
                                } else if (len > 13) {
                                    textSizeClass = 'text-sm min-[380px]:text-base sm:text-xl';
                                } else if (len > 10) {
                                    textSizeClass = 'text-base min-[380px]:text-lg sm:text-2xl';
                                }

                                return (
                                    <div 
                                        key={idx} 
                                        className="bg-[var(--color-bg-card)] rounded-2xl border border-[var(--color-border)] shadow-xs p-3.5 sm:p-4 flex flex-col justify-between hover:shadow-md transition-all min-w-0"
                                    >
                                        <div className="flex items-center justify-between gap-1.5 mb-1.5 sm:mb-2">
                                            <p className="text-[10px] sm:text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wider leading-none truncate flex-1" title={card.label}>
                                                {card.label}
                                            </p>
                                            {Icon && (
                                                <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl ${card.gradient} flex items-center justify-center flex-shrink-0 shadow-xs`}>
                                                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <p className={`${textSizeClass} font-black text-[var(--color-text-primary)] leading-tight tracking-tight break-words`} title={valStr}>
                                                {valStr}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Sequential Staff Sections grouped by Date */}
                        <div className="space-y-16">
                            {groupedReports.map(group => (
                                <div key={group.date} className="space-y-8">
                                    {/* Date Group Header */}
                                    <div className="flex items-center gap-4 print:mt-8">
                                        <div className="h-[2px] flex-1 bg-slate-100" />
                                        <div className="bg-slate-50 px-6 py-2 rounded-full border border-slate-200">
                                            <h2 className="text-sm font-black text-slate-500 uppercase tracking-[0.3em]">
                                                {format(new Date(group.date), 'EEEE, MMMM dd, yyyy')}
                                            </h2>
                                        </div>
                                        <div className="h-[2px] flex-1 bg-slate-100" />
                                    </div>

                                    <div className="space-y-12">
                                        {group.reports.map(report => {
                                            const details = detailedData[report.publicId];
                                            const distance = (details?.odometerEnd || report.odoMeterEnd || 0) - (details?.odometerStart || report.odoMeterStart || 0);
                                            
                                            return (
                                                <div key={report.publicId} className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden print:border-none print:shadow-none">
                                                    {/* Staff Header Area */}
                                                    <div className="bg-slate-50/50 p-6 border-b border-slate-100">
                                                        <div className="flex flex-wrap items-center justify-between gap-4">
                                                            <div className="flex items-center gap-4">
                                                                <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200">
                                                                    <User size={24} />
                                                                </div>
                                                                <div>
                                                                    <h3 className="text-xl font-black text-slate-800">{report.salesPersonName}</h3>
                                                                    <div className="flex items-center gap-3 text-xs font-bold text-slate-500 uppercase tracking-wider mt-0.5">
                                                                        <span className="flex items-center gap-1.5"><Hash size={12} /> {report.vehicleNo || 'NO VEHICLE'}</span>
                                                                        <span className="text-slate-300">|</span>
                                                                        <span className="flex items-center gap-1.5"><MapPin size={12} /> {details?.odometerStart || report.odoMeterStart || 0} - {details?.odometerEnd || report.odoMeterEnd || 0} km</span>
                                                                        <span className="text-slate-300">|</span>
                                                                        <span className="text-blue-600 font-black flex items-center gap-1.5">
                                                                            <TrendingUp size={12} /> {distance} KM DISTANCE
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-4">
                                                                <div className="text-right">
                                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Net Position</p>
                                                                    <p className="text-lg font-black text-blue-600">AED {report.finalNetPosition?.toFixed(2)}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="p-6 space-y-10">
                                                        {/* Sales Transactions Table */}
                                                        {(details?.salesRecords?.length > 0) && (() => {
                                                            // Column totals
                                                            const totB2bCash = details.salesRecords.reduce((s, r) => s + (Number(r.b2bCashAmount) || 0), 0);
                                                            const totB2bBank = details.salesRecords.reduce((s, r) => s + (Number(r.b2bBankAmount) || 0), 0);
                                                            const totCash    = details.salesRecords.reduce((s, r) => s + (Number(r.cashAmount)    || 0), 0);
                                                            const totBank    = details.salesRecords.reduce((s, r) => s + (Number(r.bankAmount)    || 0), 0);
                                                            const totCredit  = details.salesRecords.reduce((s, r) => s + (Number(r.creditAmount)  || 0), 0);
                                                            return (
                                                                <div className="space-y-4">
                                                                    <div className="flex items-center gap-2 px-1">
                                                                        <ShoppingBag size={14} className="text-slate-400" />
                                                                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Sales Transactions</h4>
                                                                    </div>
                                                                    <div className="border border-slate-200 rounded-2xl overflow-hidden">
                                                                        <div className="overflow-x-auto">
                                                                            <table className="w-full min-w-[620px] text-[11px] text-left border-collapse">
                                                                                <thead className="bg-blue-50/80 text-blue-800 border-y border-blue-100">
                                                                                    <tr>
                                                                                        <th className="px-4 py-3 font-black uppercase tracking-wider">Client</th>
                                                                                        <th className="px-4 py-3 font-black text-center uppercase tracking-wider whitespace-nowrap">Order / Invoice</th>
                                                                                        <th className="px-4 py-3 font-black text-right uppercase tracking-wider whitespace-nowrap">B2B Cash</th>
                                                                                        <th className="px-4 py-3 font-black text-right uppercase tracking-wider whitespace-nowrap">B2B Bank</th>
                                                                                        <th className="px-4 py-3 font-black text-right uppercase tracking-wider">Cash</th>
                                                                                        <th className="px-4 py-3 font-black text-right uppercase tracking-wider">Bank</th>
                                                                                        <th className="px-4 py-3 font-black text-right uppercase tracking-wider">Credit</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="divide-y divide-slate-100">
                                                                                    {details.salesRecords.map((s, idx) => (
                                                                                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                                                                            <td className="px-4 py-3 font-bold text-slate-800">{s.clientName}</td>
                                                                                            <td className="px-4 py-3 text-slate-500 text-center font-medium whitespace-nowrap">
                                                                                                {(() => {
                                                                                                    const orderRef = s.orderNo ? s.orderNo : null;
                                                                                                    const invRef = s.invoiceNo ? s.invoiceNo : null;
                                                                                                    if (!orderRef && !invRef) return <span className="text-slate-400">-</span>;
                                                                                                    return (
                                                                                                        <div>
                                                                                                            {orderRef && (
                                                                                                                <span className="font-bold text-slate-900 block text-xs">
                                                                                                                    <span className="text-[10px] text-blue-600 font-bold mr-1">Order</span> {orderRef.replace(/^#/, '')}
                                                                                                                </span>
                                                                                                            )}
                                                                                                            {invRef && (
                                                                                                                <span className="text-[10px] text-slate-500 block mt-0.5">
                                                                                                                    <span className="text-[9px] text-slate-400 font-normal mr-1">Inv:</span>{invRef}
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    );
                                                                                                })()}
                                                                                            </td>
                                                                                            <td className="px-4 py-3 text-right font-bold text-slate-700">{s.b2bCashAmount != null ? Number(s.b2bCashAmount).toFixed(2) : '-'}</td>
                                                                                            <td className="px-4 py-3 text-right font-bold text-slate-700">{s.b2bBankAmount != null ? Number(s.b2bBankAmount).toFixed(2) : '-'}</td>
                                                                                            <td className="px-4 py-3 text-right font-bold text-slate-700">{s.cashAmount   != null ? Number(s.cashAmount).toFixed(2)   : '-'}</td>
                                                                                            <td className="px-4 py-3 text-right font-bold text-blue-600">{s.bankAmount   != null ? Number(s.bankAmount).toFixed(2)   : '-'}</td>
                                                                                            <td className="px-4 py-3 text-right font-bold text-slate-700">{s.creditAmount != null ? Number(s.creditAmount).toFixed(2) : '-'}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                                <tfoot className="bg-slate-900 text-white">
                                                                                    <tr>
                                                                                        <td className="px-4 py-3 font-black uppercase tracking-widest text-slate-400 text-[10px]" colSpan={2}>Column Totals</td>
                                                                                        <td className="px-4 py-3 text-right font-black">{totB2bCash.toFixed(2)}</td>
                                                                                        <td className="px-4 py-3 text-right font-black">{totB2bBank.toFixed(2)}</td>
                                                                                        <td className="px-4 py-3 text-right font-black">{totCash.toFixed(2)}</td>
                                                                                        <td className="px-4 py-3 text-right font-black text-blue-300">{totBank.toFixed(2)}</td>
                                                                                        <td className="px-4 py-3 text-right font-black">{totCredit.toFixed(2)}</td>
                                                                                    </tr>
                                                                                </tfoot>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}

                                                        <div className="grid grid-cols-1 gap-10">
                                                            {/* Expenses Table */}
                                                            {(details?.expenseRecords?.length > 0) && (
                                                                <div className="space-y-4">
                                                                    <div className="flex items-center gap-2 px-1">
                                                                        <CreditCard size={14} className="text-slate-400" />
                                                                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Expenses</h4>
                                                                    </div>
                                                                    <div className="border border-slate-200 rounded-2xl overflow-hidden">
                                                                        <table className="w-full text-[11px] text-left border-collapse">
                                                                            <thead className="bg-slate-50 border-b border-slate-100">
                                                                                <tr>
                                                                                    <th className="px-5 py-3 font-black text-slate-500 uppercase tracking-wider">Description</th>
                                                                                    <th className="px-5 py-3 font-black text-right uppercase tracking-wider">Amount</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-slate-100">
                                                                                {details.expenseRecords.map((e, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td className="px-5 py-3 font-bold text-slate-800">{e.description}</td>
                                                                                        <td className="px-5 py-3 text-right font-bold text-slate-700">{e.amount?.toFixed(2)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Client Visits Table */}
                                                            {(details?.clientVisits?.length > 0) && (
                                                                <div className="space-y-4">
                                                                    <div className="flex items-center gap-2 px-1">
                                                                        <MapPin size={14} className="text-slate-400" />
                                                                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Client Visits</h4>
                                                                    </div>
                                                                    <div className="border border-slate-200 rounded-2xl overflow-hidden">
                                                                        <div className="overflow-x-auto custom-scrollbar">
                                                                            <table className="w-full min-w-[600px] text-[11px] text-left border-collapse">
                                                                                <thead className="bg-slate-50 border-b border-slate-100">
                                                                                    <tr>
                                                                                        <th className="px-5 py-3 font-black text-slate-500 uppercase tracking-wider">Client</th>
                                                                                        <th className="px-5 py-3 font-black text-center uppercase tracking-wider">Visit Type</th>
                                                                                        <th className="px-5 py-3 font-black text-center uppercase tracking-wider">Outcome</th>
                                                                                        <th className="px-5 py-3 font-black text-left uppercase tracking-wider">Activity Details</th>
                                                                                        <th className="px-5 py-3 font-black text-center uppercase tracking-wider">Follow-up</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="divide-y divide-slate-100">
                                                                                    {details.clientVisits.map((v, idx) => (
                                                                                        <tr key={idx}>
                                                                                            <td className="px-5 py-3">
                                                                                                <p className="font-bold text-slate-800">{v.clientName}</p>
                                                                                                <p className="text-[10px] text-slate-400 font-bold uppercase">{v.customerMobileNumber || '-'}</p>
                                                                                            </td>
                                                                                            <td className="px-5 py-3 text-center">
                                                                                                <span className="px-2 py-0.5 bg-slate-100 rounded text-[9px] font-black text-slate-600 uppercase">
                                                                                                    {v.visitType}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td className="px-5 py-3 text-center">
                                                                                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[9px] font-black uppercase">
                                                                                                    {v.outcomeNotes || '-'}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td className="px-5 py-3 text-left font-medium text-slate-600 max-w-[200px] truncate" title={v.notes || ''}>
                                                                                                {v.notes || '-'}
                                                                                            </td>
                                                                                            <td className="px-5 py-3 text-center font-bold text-blue-600">
                                                                                                {v.followUpDetails && !isNaN(new Date(v.followUpDetails).getTime())
                                                                                                    ? format(new Date(v.followUpDetails), 'dd/MM/yyyy')
                                                                                                    : '-'}
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Summary Footer for Staff */}
                                                    <div className="bg-slate-900 px-8 py-4 flex flex-wrap items-center justify-between gap-6 print:bg-slate-100 print:text-slate-900">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Summary: {report.salesPersonName}</p>
                                                        <div className="flex gap-8">
                                                            <div className="text-right">
                                                                <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Gross Sales</p>
                                                                <p className="text-sm font-black text-white print:text-slate-900">AED {report.totalSaleSummary?.toFixed(2)}</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Total Cash</p>
                                                                <p className="text-sm font-black text-white print:text-slate-900">AED {report.totalCashAvailable?.toFixed(2)}</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Net Position</p>
                                                                <p className="text-sm font-black text-blue-400">AED {report.finalNetPosition?.toFixed(2)}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer Notes */}
                        <div className="pt-12 border-t border-gray-100 print:pt-4">
                            <p className="text-gray-400 text-[10px] italic text-center uppercase tracking-widest">
                                --- End of Report ---
                            </p>
                        </div>
                    </div>
                ) : error ? (
                    <div className="h-64 flex flex-col items-center justify-center text-rose-500 font-bold p-6 border-2 border-dashed border-rose-500/20 rounded-2xl">
                        <XCircle size={48} className="mb-4" />
                        <p>{error}</p>
                        <button onClick={fetchData} className="mt-4 text-blue-500 hover:underline">Try Refreshing</button>
                    </div>
                ) : null}
            </div>

            {/* Print Styling */}
            <style jsx global>{`
                @media print {
                    @page {
                        size: A4;
                        margin: 1cm;
                    }
                    body {
                        background: white !important;
                        color: black !important;
                    }
                    /* Hide everything except the report container */
                    .print\\:hidden,
                    nav, 
                    header, 
                    aside,
                    .sidebar,
                    .mobile-nav {
                        display: none !important;
                    }
                    /* Ensure main content expands correctly */
                    main {
                        padding: 0 !important;
                        margin: 0 !important;
                        overflow: visible !important;
                        height: auto !important;
                    }
                    .custom-scrollbar {
                        overflow: visible !important;
                    }
                    /* Ink-saving styles for black and white printing - overrides dark backgrounds */
                    tfoot, 
                    .bg-slate-900,
                    .bg-slate-950,
                    .bg-gray-900,
                    .bg-black {
                        background-color: #f3f4f6 !important; /* light gray */
                        color: #111827 !important; /* dark text */
                    }
                    tfoot td, 
                    .bg-slate-900 *, 
                    .bg-slate-950 *, 
                    .bg-gray-900 *, 
                    .bg-black * {
                        color: #111827 !important;
                    }
                    /* Custom coloring for print since variables might be dark */
                    .text-emerald-600, .text-emerald-700 { color: #059669 !important; }
                    .text-blue-600 { color: #2563eb !important; }
                    .text-indigo-600 { color: #4f46e5 !important; }
                }
            `}</style>
        </div>
    );
};

export default DailyReportsView;
